import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '@practium/ui';
import {
	useGetOcrVerify,
	usePostOcrExtractFoliar,
	usePostOcrExtractSoil,
	type OcrFoliarSample,
	type OcrSoilSample,
} from '@practium/features/api';

import { OCR_TIMEOUT_MS, OCR_TIMEOUT_MESSAGE } from '../../api/hooks/ocr/ocr.constants';

type AnalysisType = 'soil' | 'foliar';

interface PersistedImport {
	jobId: string;
	sampleIndex: number;
	total: number;
}

const storageKey = (type: AnalysisType) => `practium:ocr-import:${type}`;

const readPersisted = (type: AnalysisType): PersistedImport | null => {
	if (typeof window === 'undefined') return null;
	try {
		const raw = window.localStorage.getItem(storageKey(type));
		if (!raw) return null;
		const parsed = JSON.parse(raw) as PersistedImport;
		return parsed?.jobId ? parsed : null;
	} catch {
		return null;
	}
};

const writePersisted = (type: AnalysisType, value: PersistedImport | null) => {
	if (typeof window === 'undefined') return;
	try {
		if (!value) window.localStorage.removeItem(storageKey(type));
		else window.localStorage.setItem(storageKey(type), JSON.stringify(value));
	} catch {
		return;
	}
};

export interface UseOcrImportResult<TSample> {
	uploadFile: (file: File) => Promise<void>;
	isUploading: boolean;
	isProcessing: boolean;
	isBusy: boolean;
	jobId: string;
	status?: 'queued' | 'processing' | 'done' | 'failed';
	samples: TSample[];
	totalSamples: number;
	currentIndex: number;
	currentSample?: TSample;
	advance: () => boolean;
	reset: () => void;
	hasPendingImport: boolean;
	loadingMessage: string;
}

export const useOcrImport = <TSample extends OcrSoilSample | OcrFoliarSample>(
	analysisType: AnalysisType,
	enabled: boolean
): UseOcrImportResult<TSample> => {
	const toast = useToast();
	const showTimeoutError = toast.error;

	const persisted = useRef<PersistedImport | null>(enabled ? readPersisted(analysisType) : null);

	const [jobId, setJobId] = useState(persisted.current?.jobId ?? '');
	const [currentIndex, setCurrentIndex] = useState(persisted.current?.sampleIndex ?? 0);

	const announcedJobId = useRef('');
	const handledFailedJobId = useRef('');

	const foliarExtract = usePostOcrExtractFoliar();
	const soilExtract = usePostOcrExtractSoil();
	const extractMutation = analysisType === 'foliar' ? foliarExtract : soilExtract;

	const { data: verify, isFetching } = useGetOcrVerify<TSample>(jobId, enabled && !!jobId);

	const status = verify?.status;
	const isUploading = extractMutation.isPending;
	const isProcessing = !!jobId && status !== 'done' && status !== 'failed';
	const isBusy = isUploading || isProcessing || (isFetching && !!jobId && status !== 'done');

	const samples = (status === 'done' ? (verify?.result?.samples ?? []) : []) as TSample[];
	const totalSamples = samples.length;
	const currentSample = samples[currentIndex];

	const persist = useCallback(
		(next: PersistedImport | null) => writePersisted(analysisType, next),
		[analysisType]
	);

	const reset = useCallback(() => {
		setJobId('');
		setCurrentIndex(0);
		announcedJobId.current = '';
		handledFailedJobId.current = '';
		persist(null);
	}, [persist]);

	useEffect(() => {
		if (!enabled || !jobId || status === 'done' || status === 'failed') return;
		const timer = window.setTimeout(() => {
			reset();
			showTimeoutError(OCR_TIMEOUT_MESSAGE);
		}, OCR_TIMEOUT_MS);
		return () => window.clearTimeout(timer);
	}, [enabled, jobId, status, reset, showTimeoutError]);

	const uploadFile = useCallback(
		async (file: File) => {
			try {
				announcedJobId.current = '';
				handledFailedJobId.current = '';
				setCurrentIndex(0);
				const run = extractMutation.mutateAsync as (vars: {
					file: File;
				}) => Promise<{ jobId: string }>;
				const response = await run({ file });
				setJobId(response.jobId);
				persist({ jobId: response.jobId, sampleIndex: 0, total: 0 });
			} catch (error) {
				console.error('Erro ao enviar arquivo para OCR:', error);
				toast.error('Não foi possível enviar o laudo. Verifique o arquivo e tente novamente.');
			}
		},
		[extractMutation, persist, toast]
	);

	const advance = useCallback(() => {
		const nextIndex = currentIndex + 1;
		if (nextIndex >= totalSamples) return false;
		setCurrentIndex(nextIndex);
		persist({ jobId, sampleIndex: nextIndex, total: totalSamples });
		return true;
	}, [currentIndex, jobId, persist, totalSamples]);

	useEffect(() => {
		if (status !== 'failed' || !verify) return;
		if (handledFailedJobId.current === verify.jobId) return;
		handledFailedJobId.current = verify.jobId;
		setJobId('');
		persist(null);
		const message = verify.error?.message?.trim();
		toast.error(
			message
				? `Falha ao ler o laudo: ${message}`
				: 'Não foi possível ler o laudo automaticamente. Preencha os campos manualmente ou tente outro arquivo.'
		);
	}, [status, verify, persist, toast]);

	useEffect(() => {
		if (status !== 'done' || !verify) return;
		if (announcedJobId.current === verify.jobId) return;

		const list = verify.result?.samples ?? [];

		if (list.length === 0) {
			announcedJobId.current = verify.jobId;
			setJobId('');
			persist(null);
			toast.error(
				analysisType === 'soil'
					? 'Nenhuma análise foi identificada. Confira se o laudo enviado é realmente de solo.'
					: 'Nenhuma análise foi identificada. Confira se o laudo enviado é realmente foliar.'
			);
			return;
		}

		if (verify.analysisType && verify.analysisType !== analysisType) {
			announcedJobId.current = verify.jobId;
			setJobId('');
			persist(null);
			toast.error(
				analysisType === 'soil'
					? 'O laudo enviado parece ser de análise foliar. Importe-o na tela de análise foliar.'
					: 'O laudo enviado parece ser de análise de solo. Importe-o na tela de análise de solo.'
			);
			return;
		}

		announcedJobId.current = verify.jobId;
		persist({ jobId: verify.jobId, sampleIndex: currentIndex, total: list.length });
		toast.info(
			list.length > 1
				? `Dados carregados (${list.length} análises). Revise e salve cada uma.`
				: 'Dados carregados. Revise os campos e salve.'
		);
	}, [status, verify, analysisType, currentIndex, persist, toast]);

	const hasPendingImport = enabled && status === 'done' && totalSamples > 0 && !!jobId;

	const loadingMessage = isUploading
		? 'Enviando o laudo para a Flora...'
		: 'A Flora está lendo o laudo, aguarde um instante...';

	return {
		uploadFile,
		isUploading,
		isProcessing,
		isBusy,
		jobId,
		status,
		samples,
		totalSamples,
		currentIndex,
		currentSample,
		advance,
		reset,
		hasPendingImport,
		loadingMessage,
	};
};
